import { lazy, Suspense, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ServerEvents from "../components/ServerEvents";
import SupportServerEvents from "../components/SupportServerEvents";
import { prefetchRoutes } from "../routes/routeChunks";
import {
  getCached,
  getStoredUser,
  getUserProfile,
  getUserStatus,
  hasUserSessionHint,
  subscribeToCacheInvalidation,
  subscribeToUserSession,
} from "../services/api";
import { getSiteUrl, updateSeo } from "../utils/seo";

const DEFAULT_DESCRIPTION =
  "Create AI-powered, ATS-optimized resumes in minutes. Use AetherCV to build role-based resumes, improve job applications, and prepare for interviews.";
const HOME_KEYWORDS =
  "AI resume builder, AetherCV, Aether CV, resume generator, AI resume, resume builder, ATS resume checker, job applications, interview preparation, role-based resume, CV builder";
const INDEX_ROBOTS =
  "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
const NOINDEX_ROBOTS = "noindex, nofollow, noarchive";

const buildWebPageStructuredData = (siteUrl, path, name, description) => {
  const baseUrl = siteUrl || "https://aethercv.com";
  const pageUrl = `${baseUrl}${path}`;
  const appUrl = `${baseUrl}/`;
  return [
    {
      id: `${path.replace(/[^a-z0-9]/gi, "") || "home"}-webpage`,
      data: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name,
        description,
        isPartOf: {
          "@type": "WebSite",
          name: "AetherCV",
          url: appUrl,
        },
        about: {
          "@type": "SoftwareApplication",
          name: "AetherCV",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: appUrl,
        },
        publisher: {
          "@type": "Organization",
          name: "AetherCV",
          url: appUrl,
          logo: `${baseUrl}/aethercv-og.png`,
        },
      },
    },
  ];
};

const buildHomeStructuredData = (siteUrl) => {
  const baseUrl = siteUrl || "https://aethercv.com";
  const appUrl = `${baseUrl}/`;
  const websiteId = `${baseUrl}/#website`;
  const softwareId = `${baseUrl}/#softwareapplication`;
  const organizationId = `${baseUrl}/#organization`;
  return [
    ...buildWebPageStructuredData(
      siteUrl,
      "/",
      "AetherCV | AI Resume Builder & ATS Optimizer",
      DEFAULT_DESCRIPTION
    ),
    {
      id: "aethercv-website",
      data: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": websiteId,
        name: "AetherCV",
        alternateName: ["Aether CV", "AetherCV AI Resume Builder"],
        url: appUrl,
        description: DEFAULT_DESCRIPTION,
        keywords: HOME_KEYWORDS,
        potentialAction: {
          "@type": "SearchAction",
          target: `${appUrl}?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
        publisher: { "@id": organizationId },
      },
    },
    {
      id: "aethercv-organization",
      data: {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": organizationId,
        name: "AetherCV",
        alternateName: "Aether CV",
        url: appUrl,
        logo: `${baseUrl}/aethercv-og.png`,
      },
    },
    {
      id: "aethercv-software",
      data: {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "@id": softwareId,
        name: "AetherCV",
        alternateName: "Aether CV",
        url: appUrl,
        description: DEFAULT_DESCRIPTION,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        isAccessibleForFree: true,
        offers: [
          {
            "@type": "Offer",
            name: "Free",
            price: "0",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: `${baseUrl}/pricing`,
          },
        ],
        featureList: [
          "AI resume builder",
          "ATS resume checks",
          "Role-based resume generation",
          "Interview preparation plans",
          "Job application assistance",
        ],
        publisher: { "@id": organizationId },
      },
    },
    {
      id: "aethercv-faq",
      data: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Is AetherCV an AI resume builder and resume generator?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes. AetherCV uses AI to generate and refine resumes, optimize ATS keywords, and guide edits for specific roles.",
            },
          },
          {
            "@type": "Question",
            name: "Does AetherCV include ATS checks?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "AetherCV includes ATS compatibility checks and scoring so you can improve parsing and relevance before applying.",
            },
          },
          {
            "@type": "Question",
            name: "Where can I see pricing?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "You can review plans on the AetherCV pricing page to compare Free, Basic, and Pro tiers.",
            },
          },
        ],
      },
    },
  ];
};

const buildSubscribeStructuredData = (siteUrl, path = "/subscribe") => {
  const baseUrl = siteUrl || "https://aethercv.com";
  const pageUrl = `${baseUrl}${path}`;
  const appUrl = `${baseUrl}/`;
  const appId = `${baseUrl}/#softwareapplication`;
  const websiteId = `${baseUrl}/#website`;
  return [
    {
      id: "subscribe-webpage",
      data: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "Pricing | AetherCV",
        description: "Compare AetherCV plans to unlock AI resume building and interview prep.",
        isPartOf: { "@id": websiteId },
        about: { "@id": appId },
        publisher: {
          "@type": "Organization",
          name: "AetherCV",
          url: appUrl,
          logo: `${baseUrl}/aethercv-og.png`,
        },
      },
    },
    {
      id: "aethercv-software",
      data: {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "@id": appId,
        name: "AetherCV",
        url: appUrl,
        description: DEFAULT_DESCRIPTION,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        isAccessibleForFree: true,
      },
    },
  ];
};

const buildIntentStructuredData = ({ siteUrl, path, name, description, faqs = [] }) => {
  const base = siteUrl || "https://aethercv.com";
  const page = buildWebPageStructuredData(siteUrl, path, name, description);
  if (!faqs.length) return page;
  return [
    ...page,
    {
      id: `${path.replace(/[^a-z0-9]/gi, "") || "page"}-faq`,
      data: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      },
    },
    {
      id: `${path.replace(/[^a-z0-9]/gi, "") || "page"}-breadcrumb`,
      data: {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: `${base}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name,
            item: `${base}${path}`,
          },
        ],
      },
    },
  ];
};

const getTitleForPath = (pathname) => {
  if (pathname === "/") return "AetherCV | AI Resume Builder, Job Applications & Interview Prep";
  if (pathname === "/features") return "Features | AetherCV";
  if (pathname === "/launchpad" || pathname === "/career-suite") return "Launchpad | AetherCV";
  if (pathname === "/ai-resume-builder") return "AI Resume Builder | AetherCV";
  if (pathname === "/job-application-assistant") return "Job Application Assistant | AetherCV";
  if (pathname === "/interview-preparation") return "Interview Preparation | AetherCV";
  if (pathname === "/register") return "Register | AetherCV";
  if (pathname === "/login") return "Login | AetherCV";
  if (pathname.startsWith("/verify-email")) return "Verify Email | AetherCV";
  if (pathname === "/forgot-password") return "Forgot Password | AetherCV";
  if (pathname.startsWith("/reset-password")) return "Reset Password | AetherCV";
  if (pathname === "/subscribe") return "Pricing | AetherCV";
  if (pathname === "/settings") return "Settings | AetherCV";
  if (pathname === "/billing") return "Billing | AetherCV";
  if (pathname === "/dashboard") return "Dashboard | AetherCV";
  if (pathname === "/setup-resume") return "Setup Resume | AetherCV";
  if (pathname === "/my-resumes") return "My Resumes | AetherCV";
  if (pathname === "/role-based-resume") return "Role Based Resume | AetherCV";
  if (pathname.startsWith("/resume-preview")) return "Resume Preview | AetherCV";
  if (pathname.startsWith("/share")) return "Shared Resume | AetherCV";
  if (pathname.startsWith("/resume-edit")) return "Edit Resume | AetherCV";
  if (pathname === "/interview-prep") return "Interview Prep | AetherCV";
  if (pathname === "/interview-plans") return "Interview Plans | AetherCV";
  if (pathname.startsWith("/mock-test")) return "Mock Test | AetherCV";
  if (pathname === "/profile") return "Profile | AetherCV";
  if (pathname === "/support") return "Support | AetherCV";
  if (pathname === "/u/support/login") return "Support Login | AetherCV";
  if (pathname === "/u/support/portal") return "Support Portal | AetherCV";
  if (pathname === "/u/support/agent") return "Support Agent | AetherCV";
  if (pathname === "/u/support/agent/login") return "Support Agent Login | AetherCV";
  if (pathname === "/u/support/admin") return "Support Admin | AetherCV";
  if (pathname === "/u/support/admin/users") return "User Analytics | AetherCV";
  if (pathname === "/u/support/admin/login") return "Support Admin Login | AetherCV";
  if (pathname === "/u/super/admin") return "Super Admin | AetherCV";
  if (pathname === "/u/super/admin/departments") return "Departments | AetherCV";
  if (pathname === "/u/super/admin/employees") return "Employees | AetherCV";
  if (pathname === "/u/super/admin/assignments") return "Assignments | AetherCV";
  if (pathname === "/u/super/admin/roles") return "Roles | AetherCV";
  if (pathname === "/u/super/admin/support") return "Support Issues | AetherCV";
  if (pathname === "/u/super/admin/users") return "User Analytics | AetherCV";
  if (pathname === "/u/super/admin/billing") return "Billing Analytics | AetherCV";
  if (pathname === "/u/super/admin/institutions") return "Institutions | AetherCV";
  if (pathname === "/u/super/admin/influencers") return "Influencers | AetherCV";
  if (pathname === "/u/super/admin/domains") return "Domains | AetherCV";
  if (pathname.startsWith("/u/super/admin/coupons")) return "Coupons | AetherCV";
  if (pathname === "/u/super/admin/ai-usage") return "AI Usage | AetherCV";
  if (pathname === "/u/admin") return "Super Admin | AetherCV";
  if (pathname === "/u/admin/departments") return "Departments | AetherCV";
  if (pathname === "/u/admin/employees") return "Employees | AetherCV";
  if (pathname === "/u/admin/assignments") return "Assignments | AetherCV";
  if (pathname === "/u/admin/roles") return "Roles | AetherCV";
  if (pathname === "/u/admin/support") return "Support Issues | AetherCV";
  if (pathname === "/u/admin/users") return "User Analytics | AetherCV";
  if (pathname === "/u/admin/billing") return "Billing Analytics | AetherCV";
  if (pathname === "/u/admin/institutions") return "Institutions | AetherCV";
  if (pathname === "/u/admin/influencers") return "Influencers | AetherCV";
  if (pathname === "/u/admin/domains") return "Domains | AetherCV";
  if (pathname.startsWith("/u/admin/coupons")) return "Coupons | AetherCV";
  if (pathname === "/u/admin/ai-usage") return "AI Usage | AetherCV";
  if (pathname === "/u/admin/login") return "Super Admin Login | AetherCV";
  return "AetherCV";
};

const SEO_ROUTES = [
  {
    match: (pathname) => pathname === "/",
    title: "AetherCV | AI Resume Builder, Job Applications & Interview Prep",
    description: DEFAULT_DESCRIPTION,
    canonicalPath: "/",
    keywords: HOME_KEYWORDS,
    robots: INDEX_ROBOTS,
    structuredData: (siteUrl) => buildHomeStructuredData(siteUrl),
  },
  {
    match: (pathname) => pathname === "/features",
    title: "Features | AetherCV AI Resume Builder",
    description:
      "Explore AetherCV features: AI resume generation, ATS optimization, role-based tailoring, job application support, and interview preparation.",
    canonicalPath: "/features",
    keywords:
      "AetherCV features, AI resume builder features, ATS resume checker, job application assistant, interview preparation software",
    robots: INDEX_ROBOTS,
    structuredData: (siteUrl) =>
      buildIntentStructuredData({
        siteUrl,
        path: "/features",
        name: "AetherCV Features",
        description:
          "Explore AetherCV features for AI resume generation, ATS optimization, job application support, and interview preparation.",
        faqs: [
          {
            question: "What are the core features in AetherCV?",
            answer:
              "AetherCV provides AI resume generation, ATS scoring, role-based edits, interview prep plans, and mock practice support.",
          },
          {
            question: "Is AetherCV built for job seekers?",
            answer:
              "Yes. AetherCV is designed to help job seekers build stronger resumes and prepare for interviews.",
          },
        ],
      }),
  },
  {
    match: (pathname) => pathname === "/launchpad" || pathname === "/career-suite",
    title: "Launchpad | AetherCV",
    description:
      "Explore AetherCV's combined career suite with AI resume building, job application workflows, and interview preparation in one place.",
    canonicalPath: "/launchpad",
    keywords:
      "AI resume, job applications, interview prep, launchpad, AetherCV workflow tools",
    robots: INDEX_ROBOTS,
    structuredData: (siteUrl) =>
      buildIntentStructuredData({
        siteUrl,
        path: "/launchpad",
        name: "Launchpad | AetherCV",
        description:
          "Use AetherCV as one combined suite for resume building, job applications, and interview preparation.",
        faqs: [
          {
            question: "Does AetherCV combine resume, applications, and interview prep?",
            answer:
              "Yes. AetherCV combines AI resume building, job application support, and interview preparation workflows in one suite.",
          },
          {
            question: "Can I access all three workflows from one section?",
            answer:
              "Yes. The Launchpad section groups AI Resume, Job Applications, and Interview Prep together.",
          },
        ],
      }),
  },
  {
    match: (pathname) => pathname === "/ai-resume-builder",
    title: "AI Resume Builder | AetherCV",
    description:
      "Build ATS-ready, role-based resumes with AetherCV AI Resume Builder. Generate, improve, and export resumes for faster job applications.",
    canonicalPath: "/ai-resume-builder",
    keywords:
      "AI resume builder, Aether CV, AetherCV AI resume builder, resume generator, ATS optimized resume, role-based resume",
    robots: INDEX_ROBOTS,
    structuredData: (siteUrl) =>
      buildIntentStructuredData({
        siteUrl,
        path: "/ai-resume-builder",
        name: "AI Resume Builder | AetherCV",
        description:
          "Build ATS-ready, role-based resumes with AetherCV AI Resume Builder for faster job applications.",
        faqs: [
          {
            question: "Is AetherCV an AI resume builder?",
            answer:
              "Yes. AetherCV uses AI to generate resume drafts, improve bullet points, and optimize ATS readability.",
          },
          {
            question: "Can AetherCV create role-based resumes?",
            answer:
              "Yes. AetherCV helps tailor resumes to specific roles and job descriptions.",
          },
        ],
      }),
  },
  {
    match: (pathname) => pathname === "/job-application-assistant",
    title: "Job Application Assistant | AetherCV",
    description:
      "Use AetherCV as an AI job application assistant for role-based resumes, ATS checks, and interview-ready preparation.",
    canonicalPath: "/job-application-assistant",
    keywords:
      "job application assistant, AI job applications, AetherCV job application support, resume and interview prep",
    robots: INDEX_ROBOTS,
    structuredData: (siteUrl) =>
      buildIntentStructuredData({
        siteUrl,
        path: "/job-application-assistant",
        name: "Job Application Assistant | AetherCV",
        description:
          "Use AetherCV as an AI-powered job application assistant with resume and interview support.",
        faqs: [
          {
            question: "How does AetherCV help with job applications?",
            answer:
              "AetherCV supports job applications through role-based resumes, ATS checks, and interview preparation guidance.",
          },
          {
            question: "Can I create multiple resumes for different roles?",
            answer:
              "Yes. AetherCV supports role-specific resume versions for multiple job openings.",
          },
        ],
      }),
  },
  {
    match: (pathname) => pathname === "/interview-preparation",
    title: "Interview Preparation | AetherCV",
    description:
      "Prepare for interviews with AetherCV interview preparation plans, mock practice workflows, and resume-aligned question strategy.",
    canonicalPath: "/interview-preparation",
    keywords:
      "interview preparation, AI interview prep, AetherCV interview preparation, mock interview practice, interview plan",
    robots: INDEX_ROBOTS,
    structuredData: (siteUrl) =>
      buildIntentStructuredData({
        siteUrl,
        path: "/interview-preparation",
        name: "Interview Preparation | AetherCV",
        description:
          "Prepare for interviews with structured plans and practice workflows from AetherCV.",
        faqs: [
          {
            question: "Does AetherCV provide interview preparation?",
            answer:
              "Yes. AetherCV provides interview prep plans and practice workflows linked to your target role.",
          },
          {
            question: "Is interview prep connected with my resume?",
            answer:
              "Yes. AetherCV aligns interview preparation with your resume content and role goals.",
          },
        ],
      }),
  },
  {
    match: (pathname) => pathname === "/subscribe",
    title: "Pricing | AetherCV",
    description:
      "Compare AetherCV plans to unlock AI resume building, ATS scoring, and interview prep.",
    canonicalPath: "/pricing",
    keywords:
      "AetherCV pricing, AI resume builder pricing, resume generator pricing, resume builder subscription, ATS resume builder plans, interview prep subscription",
    robots: INDEX_ROBOTS,
    structuredData: (siteUrl) => buildSubscribeStructuredData(siteUrl, "/pricing"),
  },
  {
    match: (pathname) => pathname === "/pricing",
    title: "Pricing | AetherCV",
    description:
      "Compare AetherCV plans to unlock AI resume building, ATS scoring, and interview prep.",
    canonicalPath: "/pricing",
    keywords:
      "AetherCV pricing, AI resume builder pricing, resume generator pricing, resume builder subscription, ATS resume builder plans, interview prep subscription",
    robots: INDEX_ROBOTS,
    structuredData: (siteUrl) => buildSubscribeStructuredData(siteUrl, "/pricing"),
  },
  {
    match: (pathname) => pathname === "/billing",
    title: "Billing | AetherCV",
    description: "Confirm your plan and apply billing discounts.",
    canonicalPath: "/billing",
    keywords: "AetherCV billing, plan checkout, coupon code, subscription billing",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/settings",
    title: "Settings | AetherCV",
    description: "Manage account details and security preferences.",
    canonicalPath: "/settings",
    keywords: "AetherCV settings, account security, change password",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/register",
    title: "Register | AetherCV",
    description:
      "Sign up for your AetherCV account to build AI-powered, ATS-ready resumes with our AI resume builder.",
    canonicalPath: "/register",
    keywords:
      "AetherCV sign up, resume builder signup, resume generator signup, create account, AI resume builder account",
    robots: NOINDEX_ROBOTS,
    structuredData: (siteUrl) =>
      buildWebPageStructuredData(
        siteUrl,
        "/register",
        "Register | AetherCV",
        "Sign up for your AetherCV account to build AI-powered, ATS-ready resumes with our AI resume builder."
      ),
  },
  {
    match: (pathname) => pathname === "/login",
    title: "Login | AetherCV",
    description:
      "Log in to AetherCV to manage AI-generated resumes, ATS checks, and interview prep.",
    canonicalPath: "/login",
    keywords:
      "AetherCV login, resume builder login, resume generator login, AI resume builder sign in, account access",
    robots: NOINDEX_ROBOTS,
    structuredData: (siteUrl) =>
      buildWebPageStructuredData(
        siteUrl,
        "/login",
        "Login | AetherCV",
        "Log in to AetherCV to manage AI-generated resumes, ATS checks, and interview prep."
      ),
  },
  {
    match: (pathname) => pathname.startsWith("/verify-email"),
    title: "Verify Email | AetherCV",
    description: "Verify your email to activate your AetherCV account.",
    canonicalPath: "/verify-email",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/forgot-password",
    title: "Forgot Password | AetherCV",
    description: "Reset your AetherCV password to regain access.",
    canonicalPath: "/forgot-password",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname.startsWith("/reset-password"),
    title: "Reset Password | AetherCV",
    description: "Set a new password for your AetherCV account.",
    canonicalPath: "/reset-password",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/support",
    title: "Support | AetherCV",
    description: "Contact AetherCV support or chat with Aether for help.",
    canonicalPath: "/support",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/super/admin",
    title: "Super Admin | AetherCV",
    description: "Full access command center for the AetherCV leadership team.",
    canonicalPath: "/u/super/admin",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/super/admin/departments",
    title: "Departments | AetherCV",
    description: "Department control room for the AetherCV leadership team.",
    canonicalPath: "/u/super/admin/departments",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/super/admin/employees",
    title: "Employees | AetherCV",
    description: "Employee management hub for the AetherCV leadership team.",
    canonicalPath: "/u/super/admin/employees",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/super/admin/assignments",
    title: "Assignments | AetherCV",
    description: "Permission and role assignments for the AetherCV leadership team.",
    canonicalPath: "/u/super/admin/assignments",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/super/admin/roles",
    title: "Roles | AetherCV",
    description: "Role stack management for the AetherCV leadership team.",
    canonicalPath: "/u/super/admin/roles",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/super/admin/support",
    title: "Support Issues | AetherCV",
    description: "Support issue dispatch and analytics for the AetherCV team.",
    canonicalPath: "/u/super/admin/support",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/super/admin/users",
    title: "User Analytics | AetherCV",
    description: "User analytics and operations for the AetherCV team.",
    canonicalPath: "/u/super/admin/users",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/super/admin/billing",
    title: "Billing Analytics | AetherCV",
    description: "Billing analytics for the AetherCV leadership team.",
    canonicalPath: "/u/super/admin/billing",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/super/admin/institutions",
    title: "Institutions | AetherCV",
    description: "Institution management for the AetherCV leadership team.",
    canonicalPath: "/u/super/admin/institutions",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/super/admin/influencers",
    title: "Influencers | AetherCV",
    description: "Influencer management for the AetherCV leadership team.",
    canonicalPath: "/u/super/admin/influencers",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/super/admin/domains",
    title: "Domains | AetherCV",
    description: "Domain registry for the AetherCV leadership team.",
    canonicalPath: "/u/super/admin/domains",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname.startsWith("/u/super/admin/coupons"),
    title: "Coupons | AetherCV",
    description: "Coupon management for the AetherCV leadership team.",
    canonicalPath: "/u/super/admin/coupons",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/super/admin/ai-usage",
    title: "AI Usage | AetherCV",
    description: "OpenAI and database usage analytics for the AetherCV team.",
    canonicalPath: "/u/super/admin/ai-usage",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/admin",
    title: "Super Admin | AetherCV",
    description: "Full access command center for the AetherCV leadership team.",
    canonicalPath: "/u/admin",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/admin/departments",
    title: "Departments | AetherCV",
    description: "Department control room for the AetherCV leadership team.",
    canonicalPath: "/u/admin/departments",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/admin/employees",
    title: "Employees | AetherCV",
    description: "Employee management hub for the AetherCV leadership team.",
    canonicalPath: "/u/admin/employees",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/admin/assignments",
    title: "Assignments | AetherCV",
    description: "Permission and role assignments for the AetherCV leadership team.",
    canonicalPath: "/u/admin/assignments",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/admin/roles",
    title: "Roles | AetherCV",
    description: "Role stack management for the AetherCV leadership team.",
    canonicalPath: "/u/admin/roles",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/admin/support",
    title: "Support Issues | AetherCV",
    description: "Support issue dispatch and analytics for the AetherCV team.",
    canonicalPath: "/u/admin/support",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/admin/users",
    title: "User Analytics | AetherCV",
    description: "User analytics and operations for the AetherCV team.",
    canonicalPath: "/u/admin/users",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/admin/billing",
    title: "Billing Analytics | AetherCV",
    description: "Billing analytics for the AetherCV leadership team.",
    canonicalPath: "/u/admin/billing",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/admin/institutions",
    title: "Institutions | AetherCV",
    description: "Institution management for the AetherCV leadership team.",
    canonicalPath: "/u/admin/institutions",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/admin/influencers",
    title: "Influencers | AetherCV",
    description: "Influencer management for the AetherCV leadership team.",
    canonicalPath: "/u/admin/influencers",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/admin/domains",
    title: "Domains | AetherCV",
    description: "Domain registry for the AetherCV leadership team.",
    canonicalPath: "/u/admin/domains",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname.startsWith("/u/admin/coupons"),
    title: "Coupons | AetherCV",
    description: "Coupon management for the AetherCV leadership team.",
    canonicalPath: "/u/admin/coupons",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/admin/ai-usage",
    title: "AI Usage | AetherCV",
    description: "OpenAI and database usage analytics for the AetherCV team.",
    canonicalPath: "/u/admin/ai-usage",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/admin/login",
    title: "Super Admin Login | AetherCV",
    description: "Secure login for the AetherCV super admin team.",
    canonicalPath: "/u/admin/login",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/support/admin",
    title: "Support Admin | AetherCV",
    description: "Support admin operations dashboard for the AetherCV team.",
    canonicalPath: "/u/support/admin",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/support/admin/users",
    title: "User Analytics | AetherCV",
    description: "User analytics and account management for the AetherCV team.",
    canonicalPath: "/u/support/admin/users",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/support/admin/login",
    title: "Support Admin Login | AetherCV",
    description: "Secure login for the AetherCV support admin team.",
    canonicalPath: "/u/support/admin/login",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/support/agent",
    title: "Support Agent | AetherCV",
    description: "Support agent workspace for the AetherCV team.",
    canonicalPath: "/u/support/agent",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname === "/u/support/agent/login",
    title: "Support Agent Login | AetherCV",
    description: "Secure login for the AetherCV support team.",
    canonicalPath: "/u/support/agent/login",
    robots: NOINDEX_ROBOTS,
  },
  {
    match: (pathname) => pathname.startsWith("/u/support"),
    title: "Support Portal | AetherCV",
    description: "Secure support portal for the AetherCV team.",
    canonicalPath: "/u/support/login",
    robots: NOINDEX_ROBOTS,
  },
];

const getSeoForPath = (pathname, siteUrl) => {
  const match = SEO_ROUTES.find((route) => route.match(pathname));
  return {
    title: match?.title || getTitleForPath(pathname),
    description: match?.description || DEFAULT_DESCRIPTION,
    canonicalPath: match?.canonicalPath || pathname || "/",
    keywords: match?.keywords,
    robots: match?.robots || NOINDEX_ROBOTS,
    structuredData:
      typeof match?.structuredData === "function"
        ? match.structuredData(siteUrl)
        : match?.structuredData,
  };
};

const getConnection = () => {
  if (typeof navigator === "undefined") return null;
  return navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
};

const shouldPrefetch = () => {
  const connection = getConnection();
  if (!connection) return true;
  if (connection.saveData) return false;
  const effectiveType = connection.effectiveType || "";
  return !["slow-2g", "2g"].includes(effectiveType);
};

const runWhenIdle = (callback, timeout = 1500) => {
  if (typeof window === "undefined") return () => {};
  if ("requestIdleCallback" in window) {
    const id = window.requestIdleCallback(callback, { timeout });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(callback, timeout);
  return () => window.clearTimeout(id);
};

const LazySupportFloatingWidget = lazy(() => import("../components/SupportFloatingWidget"));

export default function MainLayout() {
  const location = useLocation();
  const isLandingPage = location.pathname === "/";
  const interviewPlansLimit = 3;
  const [supportWidgetReady, setSupportWidgetReady] = useState(false);

  useEffect(() => {
    const seo = getSeoForPath(location.pathname, getSiteUrl());
    updateSeo(seo);
  }, [location.pathname]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!shouldPrefetch()) return undefined;
    return runWhenIdle(() => {
      if (hasUserSessionHint()) {
        prefetchRoutes([
          "/dashboard",
          "/my-resumes",
          "/profile",
          "/interview-prep",
          "/interview-plans",
        ]);
      } else {
        prefetchRoutes(["/login", "/register", "/forgot-password"]);
      }
    }, 2000);
  }, []);

  useEffect(() => {
    return subscribeToCacheInvalidation((payload) => {
      const prefixes = payload?.prefixes;
      const targets = Array.isArray(prefixes) ? prefixes : [prefixes];
      if (!targets || targets.length === 0) return;

      if (
        targets.some(
          (prefix) =>
            prefix === "*" ||
            "/user/me".startsWith(prefix) ||
            prefix.startsWith("/user/me")
        )
      ) {
        getUserProfile({ force: true }).catch(() => {});
      }
      if (
        targets.some(
          (prefix) =>
            prefix === "*" ||
            "/interview/plans".startsWith(prefix) ||
            prefix.startsWith("/interview/plans")
        )
      ) {
        getCached(
          "/interview/plans",
          { params: { limit: interviewPlansLimit } },
          { force: true }
        ).catch(() => {});
      }
    });
  }, []);

  useEffect(() => {
    const warmCache = (userId) => {
      getUserProfile({ ttl: 60000 }).catch(() => {});
      if (userId) {
        getUserStatus({ ttl: 30000 }).catch(() => {});
      }
      getCached(
        "/interview/plans",
        { params: { limit: interviewPlansLimit } },
        { ttl: 60000 }
      ).catch(() => {});
      getCached("/interview/roles", {}, { ttl: 5 * 60 * 1000 }).catch(() => {});
    };

    const scheduleWarmCache = () => {
      if (!shouldPrefetch()) return undefined;
      const userId = getStoredUser()?.id || null;
      if (!userId && !hasUserSessionHint()) return undefined;
      return runWhenIdle(() => warmCache(userId), 2000);
    };

    let cancelWarmCache = scheduleWarmCache();
    const unsubscribe = subscribeToUserSession(() => {
      cancelWarmCache?.();
      cancelWarmCache = scheduleWarmCache();
    });

    return () => {
      cancelWarmCache?.();
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!shouldPrefetch()) return;
    if (location.pathname === "/dashboard") {
      prefetchRoutes(["/resume-preview", "/my-resumes", "/role-based-resume"]);
    }
    if (location.pathname === "/login") {
      prefetchRoutes(["/forgot-password", "/register"]);
    }
    if (location.pathname === "/forgot-password") {
      prefetchRoutes(["/login"]);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (supportWidgetReady) return undefined;
    return runWhenIdle(() => setSupportWidgetReady(true), isLandingPage ? 2200 : 900);
  }, [isLandingPage, supportWidgetReady]);

  return (
    <div className="flex min-h-screen max-w-full flex-col overflow-x-hidden bg-gradient-to-tr from-[#171717] to-[#171717]">
      <ServerEvents />
      <SupportServerEvents />
      <Header showGetStarted={isLandingPage} />
      <div className="flex-1 pt-[80px]">
        {/* 80px padding matches fixed header height */}
        <Outlet />
      </div>
      <Footer />
      {supportWidgetReady ? (
        <Suspense fallback={null}>
          <LazySupportFloatingWidget />
        </Suspense>
      ) : null}
    </div>
  );
}
